import { findDocumentsByPolicyId, canRequestPolicyDocuments } from '@/services/api/document/document';

function resolveNumericValue(candidates) {
  for (let index = 0; index < candidates.length; index += 1) {
    const candidate = Number(candidates[index]);
    if (Number.isFinite(candidate)) {
      return candidate;
    }
  }

  return null;
}

function resolveAnnualPayablePremium(subscription) {
  if (!subscription) {
    return null;
  }

  const priceDetails = subscription.priceDetails || {};
  return resolveNumericValue([
    subscription.discountedPremium,
    subscription.annualPremium,
    subscription.annualPremiumTaxesIncl,
    priceDetails.annualPremium,
    priceDetails.premiumTaxesIncl,
  ]);
}

function resolveMonthlyPayablePremium(subscription) {
  if (!subscription) {
    return null;
  }

  const priceDetails = subscription.priceDetails || {};
  const explicitMonthlyPremium = resolveNumericValue([
    subscription.discountedMonthlyPremium,
    priceDetails.monthlyPremium,
  ]);

  if (explicitMonthlyPremium !== null) {
    return explicitMonthlyPremium;
  }

  const annualPayablePremium = resolveAnnualPayablePremium(subscription);
  if (annualPayablePremium === null) {
    return null;
  }

  const nbPayment = resolveNumericValue([subscription.nbPayment]);
  if (nbPayment && nbPayment > 1) {
    return annualPayablePremium / nbPayment;
  }

  return annualPayablePremium / 12;
}

function resolvePricingDisplayMode(subscription) {
  if (!subscription) {
    return { basis: null, suffix: '' };
  }

  if (subscription.plan === 'MONTHLY') {
    return { basis: 'monthly', suffix: '€ / mois' };
  }

  if (subscription.plan === 'ANNUAL') {
    return { basis: 'annual', suffix: '€ / an' };
  }

  const nbPayment = resolveNumericValue([subscription.nbPayment]);
  if (nbPayment && nbPayment > 1) {
    return { basis: 'monthly', suffix: '€ / mois' };
  }

  return { basis: 'annual', suffix: '€ / an' };
}

function formatSignedPrice(price) {
  const absolutePrice = Math.abs(price || 0);
  const sign = price < 0 ? '-' : '';
  return `${sign}${absolutePrice.toFixed(2).replace('.', ',')}`;
}

function createEmptyAmendmentPricing(subscription) {
  const { suffix } = resolvePricingDisplayMode(subscription);
  return {
    delta: 0,
    absDelta: 0,
    displayAmount: '0,00',
    displaySuffix: suffix,
    isPositive: false,
    isNegative: false,
    isZero: true,
    isReady: false,
  };
}

function resolveReferenceSubscriptionId(current, policyContext) {
  if (!current || current.status !== 'AMENDMENT' || !policyContext || !policyContext.activeVersionId) {
    return null;
  }

  if (policyContext.activeVersionId === current.id) {
    return null;
  }

  return policyContext.activeVersionId;
}

function buildAmendmentPricing(current, referenceSubscription) {
  const emptyPricing = createEmptyAmendmentPricing(current);
  if (!current || current.status !== 'AMENDMENT' || !referenceSubscription) {
    return emptyPricing;
  }

  const { basis, suffix } = resolvePricingDisplayMode(current);
  const currentPremium = basis === 'monthly'
    ? resolveMonthlyPayablePremium(current)
    : resolveAnnualPayablePremium(current);
  const referencePremium = basis === 'monthly'
    ? resolveMonthlyPayablePremium(referenceSubscription)
    : resolveAnnualPayablePremium(referenceSubscription);

  if (currentPremium === null || referencePremium === null) {
    return emptyPricing;
  }

  const roundedDelta = Number((currentPremium - referencePremium).toFixed(2));
  const delta = Math.abs(roundedDelta) < 0.01 ? 0 : roundedDelta;
  return {
    delta,
    absDelta: Math.abs(delta),
    displayAmount: formatSignedPrice(delta),
    displaySuffix: suffix,
    isPositive: delta > 0,
    isNegative: delta < 0,
    isZero: delta === 0,
    isReady: true,
  };
}

function normalizeListPayload(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  return payload && Array.isArray(payload.data) ? payload.data : [];
}

function resolvePendingAmendment(payload) {
  const amendments = normalizeListPayload(payload)
    .filter(({ status }) => status === 'AMENDMENT');

  if (amendments.length === 0) {
    return null;
  }

  return [...amendments].sort((left, right) => {
    const rightTimestamp = Date.parse(right.updatedAt || right.createdAt || '') || 0;
    const leftTimestamp = Date.parse(left.updatedAt || left.createdAt || '') || 0;

    if (rightTimestamp !== leftTimestamp) {
      return rightTimestamp - leftTimestamp;
    }

    return Number(right.versionNumber || 0) - Number(left.versionNumber || 0);
  })[0];
}

function resolvePolicyId(current, policyContext) {
  if (policyContext && policyContext.id) {
    return policyContext.id;
  }

  if (current && current.policyId) {
    return current.policyId;
  }

  if (current && current.activeVersionId) {
    return current.id;
  }

  return null;
}

async function fetchDocumentsForEntity(entity) {
  const targetId = entity.policyId || entity.id;

  if (!targetId || !canRequestPolicyDocuments(entity.status)) {
    return [];
  }

  const { data: documents } = await findDocumentsByPolicyId(targetId);
  return documents;
}

export {
  buildAmendmentPricing,
  createEmptyAmendmentPricing,
  fetchDocumentsForEntity,
  normalizeListPayload,
  resolveAnnualPayablePremium,
  resolveMonthlyPayablePremium,
  resolvePendingAmendment,
  resolvePolicyId,
  resolvePricingDisplayMode,
  resolveReferenceSubscriptionId,
};
